import React, { useState, useEffect } from 'react';
import { Button, Container } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import PipsqueakReportBuilder from './ReportBuilder';
import './ReportBuilder.css';
import { apifetch } from '../services/interceptors';
import { useUiVersion } from '../UiVersionContext';
import { useTheme } from '../ThemeContext';
import '../v2/styles/tokens.css';
import '../v2/components/primitives.css';
import './byor/v2/BuilderV2.css';
const ReportBuilderPage = () => {
  const [scanData, setScanData] = useState({});
  const [metricsData, setMetricsData] = useState(null);
  const [userData, setUserData] = useState(null);
  const [fileUrl, setFileUrl] = useState(null);
  const [initialTemplate, setInitialTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [clientInfo, setClientInfo] = useState(null);
  const [licenseType, setLicenseType] = useState('community'); // default lower tier
  const navigate = useNavigate();
  const location = useLocation();
  const { uiVersion } = useUiVersion();
  const isV2 = uiVersion === 'v2';
  const { isDarkMode } = useTheme();
  const v2Theme = isDarkMode ? ' dark' : '';

  // Determine builderMode: Query param > State > Default
  const searchParams = new URLSearchParams(location.search);
  const queryMode = searchParams.get('mode');
  const locationState = location.state || {};
  const modeFromState = locationState.mode;
  // This is the authoritative mode used for both data fetching and passing to the child
  const effectiveBuilderMode = queryMode || modeFromState;

  useEffect(() => {
    // Check if coming from PastScans with custom template
    const locationState = location.state || {};
    const searchParams = new URLSearchParams(location.search);
    const queryMode = searchParams.get('mode');

    // Determine builderMode: Query param > State > Default
    const modeFromState = locationState.mode;
    const builderMode = queryMode || modeFromState;

    // Destructure other state - now supports both internal and external scan paths
    const {
      fileUrl: passedFileUrl,
      // templateId, // OLD: only from state
      fromPastScans,
      internalScanPath,
      externalScanPath,
      hasInternalData,
      hasExternalData
    } = locationState;
    
    // Support templateId from URL query params (for page reloads)
    const templateId = locationState.templateId || searchParams.get('templateId');

    // Use passed fileUrl from PastScans, or fall back to internal scan path
    const scanFileUrl = passedFileUrl || internalScanPath;

    // Set the fileUrl
    setFileUrl(scanFileUrl);

    let cancelled = false;

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const token = localStorage.getItem('token');

        // Fetch subscription tier
        try {
          const subscriptionResponse = await apifetch('/s1/api/subscription', {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
          });
          if (subscriptionResponse.ok) {
            const subscriptionData = await subscriptionResponse.json();
            setLicenseType(subscriptionData.is_pro ? 'pro' : 'community');
          }
        } catch (subErr) {
          console.error('[ReportBuilderPage] Failed to fetch subscription:', subErr);
        }

        // Fetch user profile for MSP details (always fetch this)
        try {
          const userResponse = await apifetch('/s1/api/me', {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
          });
          if (userResponse.ok) {
            const userData = await userResponse.json();
            console.log('[ReportBuilderPage] User data loaded for MSP variables:', userData);
            setUserData(userData);
          }
        } catch (userErr) {
          console.error('[ReportBuilderPage] Failed to fetch user data:', userErr);
        }

        // CRITICAL: Skip scan data fetch in template mode ONLY if no scan file is selected
        // If scanFileUrl or externalScanPath is available, we load it so template mode can use real data
        if (effectiveBuilderMode === 'template' && !scanFileUrl && !externalScanPath) {
          console.log('[ReportBuilderPage] Template mode detected and no scan file - skipping scan data fetch');
          setScanData({});
          setMetricsData(null);
          if (!templateId) {
            setLoading(false);
            return;
          }
        }

        // Initialize combined scan data object
        let combinedScanData = {};
        let combinedMetricsData = {};

        // 1. Fetch internal scan data (if available)
        const internalPath = internalScanPath || scanFileUrl;
        if (internalPath) {
          console.log('[ReportBuilderPage] Fetching internal scan metrics from:', internalPath);
          // const testFile = '/media/mattiyoo/matthew/Dev-Storage/btsg/Pipsqueak-Network-Scanner/Website/backend/Report Management Service/routes/uploads/sample-2.json';
          const internalResponse = await apifetch('/s3/api/report-metrics', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {})
            },
            body: JSON.stringify({ fileUrl: internalPath })
          });

          let internalPayload = null;
          try {
            internalPayload = await internalResponse.json();
          } catch (parseError) {
            internalPayload = null;
          }

          if (internalResponse.ok && internalPayload) {
            const rawInternalData = internalPayload?.data || internalPayload?.scanData || {};
            // Store internal data directly at root level for backward compatibility
            // AND under 'internal' namespace for new combined reports
            combinedScanData = { ...rawInternalData };
            combinedScanData.internal = rawInternalData;
            combinedMetricsData = internalPayload;
            console.log('[ReportBuilderPage] ✅ Internal scan data loaded');
          } else if (!externalScanPath) {
            // Only throw error if we don't have external data as fallback
            const message = internalPayload?.error || 'Failed to load internal scan metrics';
            throw new Error(message);
          }
        }

        // 2. Fetch external scan data (if available)
        if (externalScanPath) {
          console.log('[ReportBuilderPage] Fetching external scan metrics from:', externalScanPath);
          const externalResponse = await apifetch('/s3/api/report-metrics', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {})
            },
            body: JSON.stringify({ fileUrl: externalScanPath })
          });

          let externalPayload = null;
          try {
            externalPayload = await externalResponse.json();
          } catch (parseError) {
            console.error('[ReportBuilderPage] Failed to parse external scan response:', parseError);
            externalPayload = null;
          }

          if (externalResponse.ok && externalPayload) {
            // The /external_risk_data endpoint returns { data: {...scan data...} }
            // The scan data has domain, categories, riskScore at root level
            let rawExternalData = null;

            // Check if data is in the expected format
            if (externalPayload?.data?.domain && externalPayload?.data?.categories) {
              rawExternalData = externalPayload.data;
              console.log('[ReportBuilderPage] External data extracted from data property');
            } else if (externalPayload?.domain && externalPayload?.categories) {
              // Direct format
              rawExternalData = externalPayload;
              console.log('[ReportBuilderPage] External data is direct format');
            } else {
              // Fallback
              rawExternalData = externalPayload?.data || externalPayload || {};
              console.log('[ReportBuilderPage] External data using fallback extraction');
            }

            // Store external data under 'external' namespace
            combinedScanData.external = rawExternalData;
            console.log('[ReportBuilderPage] ✅ External scan data loaded with keys:', Object.keys(rawExternalData));

            // Merge external metrics (if we have internal, combine them)
            if (combinedMetricsData && Object.keys(combinedMetricsData).length > 0) {
              combinedMetricsData.externalData = rawExternalData;
            } else {
              combinedMetricsData = { externalData: rawExternalData };
            }
          } else {
            console.warn('[ReportBuilderPage] Failed to load external scan data:', externalPayload?.error || 'Unknown error');
          }
        }

        if (cancelled) return;

        // Set combined scan data
        if (effectiveBuilderMode !== 'template' || Object.keys(combinedScanData).length > 0) {
          setScanData(combinedScanData);
          setMetricsData(combinedMetricsData);
          console.log('[ReportBuilderPage] Combined scan data:', {
            hasInternal: !!combinedScanData.internal,
            hasExternal: !!combinedScanData.external,
            internalKeys: Object.keys(combinedScanData.internal || {}),
            externalKeys: Object.keys(combinedScanData.external || {})
          });
        }

        // 3. Get Client Info from combined metrics data
        // Robust check: Ensure we don't set clientInfo to a null/empty object if we can avoid it
        const extractedClientInfo = combinedMetricsData?.clientInfo;
        
        if (extractedClientInfo && (extractedClientInfo.name || extractedClientInfo.email || extractedClientInfo.phone || extractedClientInfo.domain)) {
          console.log('[ReportBuilderPage] ✅ Using validated client info:', extractedClientInfo);
          setClientInfo(extractedClientInfo);
        } else {
             // Fallback logic if needed, or just warn
             console.warn('[ReportBuilderPage] ⚠️ No valid client info found in response:', extractedClientInfo);
        }

        // 2. If templateId provided, fetch the template
        if (templateId) {
          // Convert templateId to string and extract numeric ID
          const templateIdStr = String(templateId);
          const numericId = parseInt(templateIdStr.replace('custom_', ''), 10);

          console.log('[ReportBuilderPage] Loading template:', {
            templateId,
            numericId
          });

          try {
            // All templates now in report_templates - use single endpoint
            const templateResponse = await apifetch(`/s3/api/templates/${numericId}`, {
              method: 'GET',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            });

            if (!templateResponse.ok) {
              throw new Error(`Failed to load template ${numericId}: ${templateResponse.status}`);
            }

            const responseData = await templateResponse.json();
            console.log('[ReportBuilderPage] ✅ Template loaded from /api/templates');

            if (!cancelled) {
              // Map response structure to expected format
              const templateData = {
                id: responseData.template?.id || numericId,
                template_name: responseData.template?.name,
                template_data: responseData.template?.configuration,
                widget_groups: responseData.template?.widget_groups,
                global_variables: responseData.template?.global_variables
              };

              // Parse template_data if it's a string
              let parsedTemplate = templateData.template_data;
              try {
                if (typeof parsedTemplate === 'string') {
                  const temp = JSON.parse(parsedTemplate);
                  if (typeof temp === 'string') {
                    console.warn('[ReportBuilderPage] Template data parsed to a string (unexpected). Using default.');
                    parsedTemplate = {};
                  } else {
                    parsedTemplate = temp;
                  }
                }
              } catch (e) {
                console.error('[ReportBuilderPage] Failed to parse template data:', e);
                parsedTemplate = {};
              }
              
              // Ensure parsedTemplate is an object
              if (!parsedTemplate || typeof parsedTemplate !== 'object') {
                 console.warn('[ReportBuilderPage] Parsed template is not an object. Resetting.');
                 parsedTemplate = {};
              }

              // Extract visualTemplate if it's nested in configuration
              // Templates from report_templates table have structure: { visualTemplate: { pages: [...] } }
              if (parsedTemplate.visualTemplate && typeof parsedTemplate.visualTemplate === 'object') {
                console.log('[ReportBuilderPage] Extracting visualTemplate from configuration');
                parsedTemplate = parsedTemplate.visualTemplate;
              }

              // Normalize and validate the template structure
              if (!parsedTemplate.pages || !Array.isArray(parsedTemplate.pages)) {
                console.warn('[ReportBuilderPage] Invalid pages structure in template, creating default page');
                parsedTemplate.pages = [{ id: 'page-1', name: 'Page 1', blocks: [] }];
                
                if (templateData.template_name) {
                    parsedTemplate.name = templateData.template_name;
                }
              }

              // Inject the DB ID so ReportBuilder knows this is an existing template
              parsedTemplate.id = numericId;

              // PERFORMANCE: For large templates (50+ pages), do only basic normalization
              // The heavy pagination normalization will be handled by ReportBuilder's useEffect
              const isLargeTemplate = parsedTemplate.pages.length > 30;
              const isExtraLargeTemplate = parsedTemplate.pages.length > 100; // 100+ pages = ULTRA mode

              console.log(`[ReportBuilderPage] Template has ${parsedTemplate.pages.length} pages. isLargeTemplate: ${isLargeTemplate}, isExtraLargeTemplate: ${isExtraLargeTemplate}`);

              // PERFORMANCE: For ULTRA large templates (100+ pages), do minimal normalization
              // Only normalize page IDs and names, skip deep block normalization to prevent main thread freeze
              let normalizedPages;

              if (isExtraLargeTemplate) {
                console.log('[ReportBuilderPage] ULTRA mode: Minimal normalization for 100+ pages');
                // Ultra-fast shallow normalization - just ensure basic structure
                // This prevents the main thread from freezing on 200+ page templates
                normalizedPages = parsedTemplate.pages.map((page, pageIndex) => ({
                  id: page.id || `page-${pageIndex + 1}`,
                  name: page.name || `Page ${pageIndex + 1}`,
                  blocks: page.blocks || [], // Keep blocks as-is, no deep normalization
                  isContinuationPage: page.isContinuationPage || false,
                  continuationOf: page.continuationOf || null,
                  continuationPageIndex: page.continuationPageIndex || null,
                  continuationForPage: page.continuationForPage || null,
                  // Preserve placeholder metadata
                  isPlaceholder: page.isPlaceholder || false,
                  placeholderForPage: page.placeholderForPage || null,
                  // Preserve page config (used for dependentBlockId etc.)
                  config: page.config || {}
                }));
              } else {
                // Normal normalization for templates under 100 pages
                normalizedPages = parsedTemplate.pages.map((page, pageIndex) => {
                  // Check if this page has cloned blocks - if so, it MUST be a continuation page
                  const hasClonedBlocks = Array.isArray(page.blocks) && page.blocks.some(b => b.isClonedBlock);
                  // Also check page name as fallback indicator (some templates may have continuation pages without proper flags)
                  const hasContinuationName = page.name && page.name.toLowerCase().includes('continuation');

                  // Ensure page has required properties
                  const normalizedPage = {
                    id: page.id || `page-${pageIndex + 1}`,
                    name: page.name || `Page ${pageIndex + 1}`,
                    blocks: Array.isArray(page.blocks) ? page.blocks : [],
                    // CRITICAL: If page has cloned blocks OR name indicates continuation, mark as continuation page
                    // This ensures continuation pages from templates are always properly marked
                    isContinuationPage: hasClonedBlocks ? true : (page.isContinuationPage || hasContinuationName || false),
                    continuationOf: page.continuationOf || null,
                    continuationPageIndex: page.continuationPageIndex || null,
                    continuationForPage: page.continuationForPage || null,
                    // Preserve placeholder metadata
                    isPlaceholder: page.isPlaceholder || false,
                    placeholderForPage: page.placeholderForPage || null,
                    // Preserve page config (used for dependentBlockId etc.)
                    config: page.config || {}
                  };

                  // Normalize each block to ensure proper structure
                  const normalizeBlock = (block, blockIndex) => {
                    const normalizedBlock = {
                      id: block.id || `block-${Date.now()}-${blockIndex}`,
                      type: block.type || 'widget',
                      position: block.position || { x: 0, y: 0 },
                      size: block.size || { width: 200, height: 150 },
                      config: block.config || {},
                      style: block.style || {},
                      dataConfig: block.dataConfig || {},
                      customData: block.customData || {},
                      pagination: block.pagination ? { ...block.pagination } : null,
                      isStaticClone: block.isStaticClone || false,
                      isClonedBlock: block.isClonedBlock || false,
                      originalBlockId: block.originalBlockId || null,
                      manualResize: block.manualResize || false,
                      children: Array.isArray(block.children) ? block.children.map((child, idx) => normalizeBlock(child, idx)) : []
                    };

                    // CRITICAL: Continuation blocks (cloned blocks on continuation pages) should start at y: 0
                    // This ensures they appear at the top of continuation pages
                    if (normalizedBlock.isClonedBlock && normalizedPage.isContinuationPage) {
                      normalizedBlock.position = {
                        x: normalizedBlock.position.x || 0,
                        y: 0 // Always start at top of continuation page
                      };
                    }

                    // Preserve widget/element type
                    if (block.widgetType) {
                      normalizedBlock.widgetType = block.widgetType;
                    }
                    if (block.elementType) {
                      normalizedBlock.elementType = block.elementType;
                    }
                    // Preserve placeholder metadata on blocks
                    if (block.isPlaceholderWidget) {
                      normalizedBlock.isPlaceholderWidget = true;
                      normalizedBlock.placeholderForWidget = block.placeholderForWidget || null;
                    }

                    return normalizedBlock;
                  };

                  normalizedPage.blocks = normalizedPage.blocks.map((block, blockIndex) => normalizeBlock(block, blockIndex));

                  return normalizedPage;
                });
              }

              // PERFORMANCE: Skip heavy second pass normalization for large templates
              // ReportBuilder's useEffect will handle pagination normalization after template loads
              // ALSO skip for extra large templates to prevent freeze
              if (!isLargeTemplate && !isExtraLargeTemplate) {
                console.log('[ReportBuilderPage] Running second pass normalization for small template');

                // Build a fast lookup map for original blocks (O(1) access instead of O(n) search)
                const originalBlocksMap = new Map();
                normalizedPages.forEach((page) => {
                  if (!page.isContinuationPage) {
                    page.blocks.forEach(block => {
                      originalBlocksMap.set(block.id, block);
                    });
                  }
                });

                // Second pass: Fix pagination for continuation blocks by finding original blocks
                normalizedPages = normalizedPages.map((page) => {
                  if (page.isContinuationPage) {
                    page.blocks = page.blocks.map((block) => {
                      if (block.isClonedBlock && block.originalBlockId) {
                        // OPTIMIZED: Use Map lookup instead of nested loop (O(1) instead of O(n))
                        const originalBlock = originalBlocksMap.get(block.originalBlockId);

                        if (originalBlock) {
                          // Get pagination values from original block's dataConfig or pagination
                          let originalItemsPerPageFirstPage =
                            originalBlock.dataConfig?.itemsPerPageFirstPage ||
                            originalBlock.dataConfig?.itemsPerPage ||
                            originalBlock.pagination?.itemsPerPageFirstPage ||
                            originalBlock.pagination?.itemsPerPage ||
                            15;
                          if (originalItemsPerPageFirstPage === 'auto') {
                            originalItemsPerPageFirstPage = 15;
                          }

                          let originalItemsPerPageContinuation =
                            originalBlock.dataConfig?.itemsPerPageContinuation ||
                            originalBlock.dataConfig?.itemsPerPage ||
                            originalBlock.pagination?.itemsPerPageContinuation ||
                            originalBlock.pagination?.itemsPerPage ||
                            15;
                          if (originalItemsPerPageContinuation === 'auto') {
                            originalItemsPerPageContinuation = 15;
                          }

                          // Initialize pagination if it doesn't exist
                          if (!block.pagination) {
                            block.pagination = {};
                          }

                          // CRITICAL: Set currentPage based on continuationPageIndex (1-indexed)
                          // If continuationPageIndex is undefined, try to infer from page order or use 1 as default
                          if (page.continuationPageIndex !== undefined && page.continuationPageIndex > 0) {
                            block.pagination.currentPage = page.continuationPageIndex;
                          } else {
                            // Fallback: try to infer page index from page order
                            // Find all continuation pages for this block and determine index
                            const allContinuationPages = normalizedPages.filter(p =>
                              p.isContinuationPage && p.continuationOf === block.originalBlockId
                            ).sort((a, b) => {
                              const aIdx = normalizedPages.indexOf(a);
                              const bIdx = normalizedPages.indexOf(b);
                              return aIdx - bIdx;
                            });
                            const pageIndex = allContinuationPages.indexOf(page);
                            block.pagination.currentPage = pageIndex >= 0 ? pageIndex + 1 : 1;
                          }

                          // Set itemsPerPageFirstPage from original block (CRITICAL for startIndex calculation)
                          block.pagination.itemsPerPageFirstPage = originalItemsPerPageFirstPage;

                          // Set itemsPerPageContinuation from original block
                          block.pagination.itemsPerPageContinuation = originalItemsPerPageContinuation;

                          // Update itemsPerPage to use continuation value for continuation pages
                          block.pagination.itemsPerPage = originalItemsPerPageContinuation;

                          // Preserve totalItems and totalPages from original block if available
                          if (originalBlock.pagination?.totalItems !== undefined) {
                            block.pagination.totalItems = originalBlock.pagination.totalItems;
                          }
                          if (originalBlock.pagination?.totalPages !== undefined) {
                            block.pagination.totalPages = originalBlock.pagination.totalPages;
                          }

                          // Ensure spansMultiplePages is set
                          block.pagination.spansMultiplePages = true;
                        }
                      }
                      return block;
                    });
                  }
                  return page;
                });
              }

              parsedTemplate.pages = normalizedPages;

              // Ensure theme exists
              if (!parsedTemplate.theme) {
                parsedTemplate.theme = {
                  primaryColor: '#3b82f6',
                  secondaryColor: '#1e40af',
                  logo: null,
                  header: 'Pipsqueak Security Report',
                  footer: 'Confidential - Generated by Pipsqueak',
                  companyName: '',
                  watermark: false
                };
              }

              console.log('[ReportBuilderPage] ✅ Normalized custom template loaded:', {
                pageCount: parsedTemplate.pages.length,
                totalBlocks: parsedTemplate.pages.reduce((sum, p) => sum + p.blocks.length, 0),
                hasWidgetGroups: !!templateData.widget_groups,
                pages: parsedTemplate.pages.map(p => ({
                  id: p.id,
                  name: p.name,
                  blockCount: p.blocks.length,
                  isContinuationPage: p.isContinuationPage
                }))
              });

              // CRITICAL: Include widget_groups from the template data
              // This is needed for dynamic widget positioning
              if (templateData.widget_groups) {
                parsedTemplate.widget_groups = templateData.widget_groups;
              }

              setInitialTemplate(parsedTemplate);
            }
          } catch (templateError) {
            console.error('[ReportBuilderPage] Error loading template:', templateError);
            setError(`Failed to load template: ${templateError.message}`);
          }
        }

      } catch (err) {
        if (!cancelled) {
          console.error('[ReportBuilderPage] Error:', err);
          setError(err.message || 'Failed to load report data');
          setScanData({});
          setMetricsData(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [location.state]);

  const handleBackToScans = () => {
    navigate('/dashboard');
  };

  const handleSaveTemplate = (template) => {
    console.log('Template saved:', template);
    // Handle template saving logic here
    // You can implement save to backend, local storage, etc.
  };

  const handleExportReport = (report) => {
    console.log('Report exported:', report);
    // Handle report export logic here
    // You can implement PDF generation, download, etc.
  };

  if (loading && isV2) {
    return (
      <div className={`v2-tokens rb2-loading${v2Theme}`} role="status">
        <span className="rb2-spinner" aria-hidden="true" />
        Loading report builder…
      </div>
    );
  }

  if (error && isV2) {
    return (
      <div className={`v2 rb2-loading${v2Theme}`}>
        <b style={{ color: 'var(--v2-text)', fontSize: '.875rem' }}>Unable to load report</b>
        <span>{error}</span>
        <button type="button" className="v2-btn v2-btn--primary" onClick={() => navigate(-1)}>Go back</button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading Report Builder...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Container className="py-5">
        <div className="text-center">
          <h4>Unable to Load Report</h4>
          <p className="text-muted">{error}</p>
          <Button variant="primary" onClick={handleBackToScans}>
            Back to Scan Results
          </Button>
        </div>
      </Container>
    );
  }

  const builder = (
    <PipsqueakReportBuilder
      scanData={scanData}
      filePath={fileUrl}
      initialMetricsData={metricsData}
      initialTemplate={initialTemplate}
      onSaveTemplate={handleSaveTemplate}
      onExportReport={handleExportReport}
      mode={effectiveBuilderMode}
      mspInfo={userData}
      clientInfo={clientInfo}
      readOnly={licenseType === 'community'}
      autoOpenAI={!!locationState.autoOpenAI}
    />
  );

  // v2: the builder draws its own header (back, title, actions), so it takes the whole
  // viewport rather than sitting under this page's.
  if (isV2) {
    return <div style={{ height: '100vh', overflow: 'hidden' }}>{builder}</div>;
  }

  return (
    <div style={{ height: '100vh', overflow: 'hidden' }}>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center p-3 border-bottom bg-white shadow-sm">
        <div>
          <h4 className="mb-0">Custom Report Builder</h4>
          <small className="text-muted">Create a custom report from your scan data</small>
        </div>
        <Button
          variant="outline-secondary"
          onClick={handleBackToScans}
        >
          ← Back to Scan Results
        </Button>
      </div>

      {/* Report Builder */}
      <div style={{ height: 'calc(100vh - 80px)' }}>
        {builder}
      </div>
    </div>
  );
};

export default ReportBuilderPage;
